import config from 'config';
import { dispatch } from 'store';
import { saveUserPromoCodes } from 'store/actions/user-promo-code-service.actions';
import { extractCheckoutErrorCode } from 'utils/checkoutError';
import i18n from 'utils/i18n';
import { isReferralErrorCode } from 'utils/promo-code';
import { GET, POST } from 'utils/request';
import { formatResponse } from 'utils/service-helpers';
import { notification } from 'utils/snackbar';

export class UserPromoCodeService {
  private USER_PROMO_CODE_URL = `${config.api?.url}/user-promo-code`;
  private inviteLinkRequests = new Map<string, Promise<boolean>>();
  private lastInviteLink: { code: string; result: Promise<boolean> } | null = null;

  async updateStore() {
    try {
      const promoCodes = await this.fetch();

      if (Array.isArray(promoCodes)) {
        dispatch(saveUserPromoCodes(promoCodes));
      }

      return promoCodes;
    } catch (error) {
      console.error('Failed to fetch User Promo Codes', error);
    }
  }

  async fetch() {
    const response = await GET(this.USER_PROMO_CODE_URL);

    return formatResponse(response);
  }

  async register(code: string) {
    const response = await POST(`${this.USER_PROMO_CODE_URL}`, { code }, { silentFail: true });

    return formatResponse(response);
  }

  /** Links an invite link's code and says how it went. Resolves to whether the code was accepted. */
  registerFromInviteLink(code: string) {
    if (!this.inviteLinkRequests.has(code)) {
      this.inviteLinkRequests.set(
        code,
        this.register(code)
          .then(() => {
            notification.showSuccessNotification(i18n.t('pages.promocodes.invite-code-applied'));

            return true;
          })
          .catch((error) => {
            // Only the buyer programme's refusals are shown; a legacy seller link, a 429 or a network blip stays quiet.
            if (isReferralErrorCode(extractCheckoutErrorCode(error))) {
              notification.showErrorNotification({ error });
            }

            return false;
          })
          .finally(() => this.updateStore()),
      );
    }

    const result = this.inviteLinkRequests.get(code);

    this.lastInviteLink = { code, result };

    return result;
  }

  /** The invite link code applied since the last call, so the promo codes page can offer a retry once. */
  takeLastInviteLink() {
    const lastInviteLink = this.lastInviteLink;

    this.lastInviteLink = null;

    return lastInviteLink;
  }
}

export const userPromoCodeService = new UserPromoCodeService();
